import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Calendar,
  CheckCircle2,
  Clock,
  Loader2,
  Pause,
  PenLine,
  Rocket,
  Settings2,
  Users,
  XCircle,
} from "lucide-react";
import OperatorBalanceRing from "../components/operator/OperatorBalanceRing";
import OperatorConsoleShell from "../components/operator/OperatorConsoleShell";
import OperatorConsoleSkeleton from "../components/operator/OperatorConsoleSkeleton";
import OperatorNoHouseHero from "../components/operator/OperatorNoHouseHero";
import OperatorNoHousePanel from "../components/operator/OperatorNoHousePanel";
import HouseBrandModal from "../components/operator/HouseBrandModal";
import HouseLimitsModal from "../components/operator/HouseLimitsModal";
import HouseGoLiveModal from "../components/operator/HouseGoLiveModal";
import TreasuryMoveModal from "../components/operator/TreasuryMoveModal";
import {
  OperatorActivityPanel,
  OperatorBettorsPanel,
  OperatorPulsePanel,
  OperatorTreasuryPanel,
} from "../components/operator/OperatorSections";
import OperatorLoanPanel from "../components/operator/OperatorLoanPanel";
import OperatorMatchingPanel from "../components/operator/OperatorMatchingPanel";
import OperatorPartnersPanel from "../components/operator/OperatorPartnersPanel";
import OperatorRiskPanel from "../components/operator/OperatorRiskPanel";
import { selectAppUser } from "../redux/appAuthSlice";
import {
  clearOperatorDashboard,
  fetchGoLiveReadiness,
  fetchHousePartners,
  fetchOperatorDashboard,
  fetchOperatorHouses,
  updateBetHouseStatus,
} from "../redux/operatorConsoleSlice";
import {
  formatCash,
  OPERATOR_MANAGE_HOME,
  operatorDashboardForHouse,
  resolveOperatorHouse,
  shouldSyncDashboard,
} from "../utils/operatorConsoleHelpers";
import { isBetHousePartnershipReadyForConsole, hasPendingPartnerRequest } from "../utils/betHousePartners";
import "../operator-console.css";

const MANAGE_HOME = OPERATOR_MANAGE_HOME;

const OperatorConsole = () => {
  const { houseId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectAppUser);
  const userId = user?._id;

  const {
    houses,
    housesStatus,
    housesError,
    dashboard,
    dashboardHouseId,
    dashboardStatus,
    dashboardError,
    dashboardsByHouse,
    dashboardStatusByHouse,
    housePartnersData,
    housePartnersHouseId,
  } = useSelector((s) => s.operatorConsole);

  const [activeSection, setActiveSection] = useState(
    () => location.state?.section || "pulse"
  );
  const [brandOpen, setBrandOpen] = useState(false);
  const [limitsOpen, setLimitsOpen] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [goLiveOpen, setGoLiveOpen] = useState(false);
  const [goLiveReadiness, setGoLiveReadiness] = useState(null);
  const [goLiveLoading, setGoLiveLoading] = useState(false);
  const [goLiveError, setGoLiveError] = useState(null);
  const [statusToast, setStatusToast] = useState(null);
  const [treasuryMove, setTreasuryMove] = useState(null);
  const bet = useMemo(
    () =>
      houseId
        ? houses.find((h) => String(h._id) === String(houseId)) || null
        : null,
    [houses, houseId]
  );
  const displayBet = bet || resolveOperatorHouse(houses, houseId);

  const noHouse = housesStatus === "succeeded" && houses.length === 0;
  const housesLoading =
    (housesStatus === "loading" || housesStatus === "idle") && !houses.length;

  const statusKey = String(displayBet?.status || "draft").toLowerCase();
  const syncDashboard = shouldSyncDashboard(statusKey);
  const {
    dashboard: dashboardForDisplay,
    isStale: dashboardStale,
    isRefreshing: dashboardRefreshing,
  } = useMemo(
    () =>
      operatorDashboardForHouse({
        dashboard,
        dashboardHouseId,
        dashboardsByHouse,
        houseId,
        dashboardStatus,
        dashboardStatusByHouse,
      }),
    [
      dashboard,
      dashboardHouseId,
      dashboardsByHouse,
      houseId,
      dashboardStatus,
      dashboardStatusByHouse,
    ]
  );
  const dashboardReady =
    dashboardForDisplay &&
    (Boolean(dashboardsByHouse[String(houseId)]) ||
      String(dashboardHouseId) === String(houseId));

  const loadDashboard = useCallback(() => {
    if (!houseId || !syncDashboard) {
      dispatch(clearOperatorDashboard());
      return Promise.resolve();
    }
    return dispatch(fetchOperatorDashboard(houseId));
  }, [dispatch, houseId, syncDashboard]);

  useEffect(() => {
    if (housesStatus !== "succeeded" || !houses.length) return;
    const target = resolveOperatorHouse(houses, houseId);
    if (!target) return;
    if (!houseId || String(houseId) !== String(target._id)) {
      navigate(`${MANAGE_HOME}/${target._id}`, { replace: true });
    }
  }, [housesStatus, houses, houseId, navigate]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (!houseId || !userId) return;
    dispatch(fetchHousePartners({ betId: houseId, operatorUserId: userId }));
  }, [dispatch, houseId, userId]);

  useEffect(() => {
    setGoLiveOpen(false);
    setGoLiveReadiness(null);
    setGoLiveError(null);
    setGoLiveLoading(false);
    setStatusBusy(false);
    setTreasuryMove(null);
  }, [houseId]);

  useEffect(() => {
    if (!statusToast) return undefined;
    const timer = window.setTimeout(() => setStatusToast(null), 4200);
    return () => window.clearTimeout(timer);
  }, [statusToast]);

  useEffect(() => {
    if (location.state?.section) {
      setActiveSection(location.state.section);
    }
  }, [location.state?.section]);

  const partnerData =
    housePartnersHouseId && String(housePartnersHouseId) === String(houseId)
      ? housePartnersData
      : null;
  const consoleActionsLocked = hasPendingPartnerRequest(partnerData);

  const handleRefresh = async () => {
    if (userId) {
      await dispatch(fetchOperatorHouses(userId));
    }
    await loadDashboard();
    if (houseId && userId) {
      await dispatch(fetchHousePartners({ betId: houseId, operatorUserId: userId }));
    }
  };

  const showStatusToast = (type, title, detail) => {
    setStatusToast({ type, title, detail });
  };

  const handleTreasurySuccess = ({ message, mode }) => {
    setTreasuryMove(null);
    showStatusToast(
      "success",
      mode === "deposit" ? "Deposit complete" : "Withdrawal complete",
      message
    );
    handleRefresh();
  };

  const openGoLiveChecklist = async () => {
    if (consoleActionsLocked) {
      showStatusToast(
        "info",
        "Partner invite waiting",
        "Console buttons stay locked until the invite is accepted, cancelled, or expired."
      );
      return;
    }
    if (!userId || !houseId || goLiveLoading) return;

    setGoLiveError(null);
    setGoLiveOpen(true);
    setGoLiveLoading(true);
    setGoLiveReadiness(null);
    try {
      const data = await dispatch(
        fetchGoLiveReadiness({ betId: houseId, userId })
      ).unwrap();
      setGoLiveReadiness(data);
    } catch (err) {
      const msg =
        (typeof err === "object" && (err?.message || err?.payload?.message)) ||
        "Could not load checklist.";
      setGoLiveError(msg);
    } finally {
      setGoLiveLoading(false);
    }
  };

  const handleStatusChange = async (nextStatus) => {
    if (consoleActionsLocked) {
      showStatusToast(
        "info",
        "Partner invite waiting",
        "Console buttons stay locked until the invite is accepted, cancelled, or expired."
      );
      return;
    }
    if (!userId || !houseId || statusBusy) return;

    if (nextStatus === "active") {
      await openGoLiveChecklist();
      return;
    }

    setStatusBusy(true);
    try {
      await dispatch(
        updateBetHouseStatus({ betId: houseId, userId, status: nextStatus })
      ).unwrap();
      showStatusToast("success", "Status updated", `House is now ${nextStatus}.`);
      await loadDashboard();
    } catch (err) {
      showStatusToast(
        "error",
        "Failed to change status",
        err?.message || "Please try again."
      );
    } finally {
      setStatusBusy(false);
    }
  };

  const confirmGoLive = async () => {
    if (!userId || !houseId || statusBusy || !goLiveReadiness?.canActivate) return;

    setStatusBusy(true);
    try {
      await dispatch(
        updateBetHouseStatus({ betId: houseId, userId, status: "active" })
      ).unwrap();
      setGoLiveOpen(false);
      setGoLiveReadiness(null);
      showStatusToast(
        "success",
        "House is live",
        "Bettors can now discover this house where it is listed."
      );
      if (userId) {
        await dispatch(fetchOperatorHouses(userId));
      }
      await loadDashboard();
    } catch (err) {
      const readiness = err?.readiness;
      if (readiness?.checks) {
        setGoLiveReadiness(readiness);
      }
      showStatusToast(
        "error",
        "Cannot go live",
        err?.message || "Please fix the checklist items and try again."
      );
    } finally {
      setStatusBusy(false);
    }
  };

  const treasury = dashboardReady ? dashboardForDisplay?.treasury : null;
  const available = treasury?.availableBalance ?? displayBet?.amountAvailable ?? 0;
  const locked = treasury?.lockedLiability ?? 0;
  const pending = treasury?.pendingBalanceBook ?? displayBet?.pendingBalance ?? 0;
  const partnershipReady = isBetHousePartnershipReadyForConsole(displayBet);
  const canUseConsoleActions = partnershipReady && !consoleActionsLocked && String(displayBet?.status || "") !== "closed";
  const isHouseClosed = String(displayBet?.status || "") === "closed";
  const isHouseActive = statusKey === "active";
  const upcomingFixturesCount = Number(
    dashboardReady
      ? dashboardForDisplay?.fixtures?.upcomingNotStarted ?? dashboardForDisplay?.fixtures?.active ?? 0
      : displayBet?.activeFixturesCount ?? 0
  );
  const fixturesPath = `/app/bethouse/manage/${houseId}/fixtures`;

  const renderSection = () => {
    switch (activeSection) {
      case "pulse":
        return (
          <OperatorPulsePanel
            bet={displayBet}
            dashboard={dashboardForDisplay}
            onGoTreasury={() => setActiveSection("treasury")}
            bettorsPath={`/app/bethouse/manage/${houseId}/bettors`}
            ticketsPath={`/app/bethouse/manage/${houseId}/tickets`}
            viewersPath={`/app/bethouse/manage/${houseId}/viewers`}
            followersPath={`/app/bethouse/manage/${houseId}/followers`}
          />
        );
      case "treasury":
        return (
          <OperatorTreasuryPanel
            bet={displayBet}
            dashboard={dashboardForDisplay}
            operatorUserId={userId}
            onOpenDeposit={() => setTreasuryMove("deposit")}
            onOpenWithdraw={() => setTreasuryMove("withdraw")}
          />
        );
      case "bettors":
        return <OperatorBettorsPanel dashboard={dashboardForDisplay} houseId={houseId} />;
      case "activity":
        return (
          <OperatorActivityPanel
            dashboard={dashboardForDisplay}
            onGoTreasury={() => setActiveSection("treasury")}
            ticketsPath={`/app/bethouse/manage/${houseId}/tickets`}
          />
        );
      case "loan":
        return (
          <OperatorLoanPanel
            bet={displayBet}
            operatorUserId={userId}
            onLoanSuccess={handleRefresh}
          />
        );
      case "partners":
        return (
          <OperatorPartnersPanel
            bet={displayBet}
            operatorUserId={userId}
            onPartnersChange={handleRefresh}
            onLeaveHouse={() => navigate(MANAGE_HOME)}
          />
        );
      case "matching":
        return (
          <OperatorMatchingPanel
            bet={displayBet}
            operatorUserId={userId}
            houseId={houseId}
            onOpenLimits={() => setLimitsOpen(true)}
          />
        );
      case "risk":
        return (
          <OperatorRiskPanel
            bet={displayBet}
            dashboard={dashboardForDisplay}
            houseId={houseId}
            loading={dashboardRefreshing && !dashboardForDisplay}
            onRefresh={handleRefresh}
          />
        );
      default:
        return null;
    }
  };

  if (noHouse) {
    return (
      <OperatorConsoleShell activeSection="pulse">
        <OperatorNoHouseHero />
        <div className="oc-stage oc-stage-enter">
          <OperatorNoHousePanel />
        </div>
      </OperatorConsoleShell>
    );
  }

  if (!displayBet && housesLoading) {
    return (
      <OperatorConsoleShell activeSection="pulse">
        <OperatorConsoleSkeleton message="Loading your markets…" />
      </OperatorConsoleShell>
    );
  }

  if (!displayBet) {
    return null;
  }

  return (
    <>
      <OperatorConsoleShell
        activeSection={activeSection}
        onSectionChange={setActiveSection}
      >
          <header className="oc-hero">
            <div className="oc-hero-copy">
              <p className="oc-eyebrow">House treasury</p>
              <h1 className="tabular-nums">
                {formatCash(available + locked + pending, { compact: true })}
              </h1>
              <span>Total house balance</span>
            </div>

            <OperatorBalanceRing
              available={available}
              locked={locked}
              pending={pending}
              onClick={() => setActiveSection("treasury")}
            />

            <div className="oc-hero-pools">
              <div>
                <em>Available</em>
                <strong className="tabular-nums">{formatCash(available, { compact: true })}</strong>
              </div>
              <div>
                <em>Locked</em>
                <strong className="tabular-nums">{formatCash(locked, { compact: true })}</strong>
              </div>
              <div>
                <em>Pending</em>
                <strong className="tabular-nums">{formatCash(pending, { compact: true })}</strong>
              </div>
            </div>

            <div className="oc-hero-actions">
              {!isHouseClosed ? (
                isHouseActive ? (
                  <button
                    type="button"
                    className="oc-hero-action oc-hero-action-danger"
                    disabled={statusBusy || consoleActionsLocked}
                    title={
                      consoleActionsLocked
                        ? "Partner invite waiting"
                        : "Pause this house"
                    }
                    onClick={() => handleStatusChange("paused")}
                  >
                    {statusBusy ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Pause size={14} />
                    )}
                    {statusBusy ? "Pausing…" : "Pause"}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="oc-hero-action oc-hero-action-success"
                    disabled={statusBusy || goLiveLoading || consoleActionsLocked}
                    title={
                      consoleActionsLocked
                        ? "Partner invite waiting"
                        : partnershipReady
                          ? "Run checklist and go live"
                          : "Waiting on co-owners"
                    }
                    onClick={() => handleStatusChange("active")}
                  >
                    {statusBusy || goLiveLoading ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Rocket size={14} />
                    )}
                    {statusBusy
                      ? "Publishing…"
                      : goLiveLoading
                        ? "Checking…"
                        : "Go live"}
                  </button>
                )
              ) : null}
            </div>

            {!isHouseClosed && consoleActionsLocked ? (
              <div className="oc-notice-banner">
                <Clock size={15} aria-hidden />
                <div>
                  <strong>Partner invite waiting</strong>
                  <p>
                    Console buttons stay locked until the invite is accepted,
                    cancelled, or expired.
                  </p>
                </div>
              </div>
            ) : !isHouseClosed && !partnershipReady ? (
              <div className="oc-notice-banner">
                <Users size={15} aria-hidden />
                <div>
                  <strong>Waiting on co-owners</strong>
                  <p>
                    Top up, withdraw, fixtures, odds, and limits stay locked until
                    every invited partner accepts.
                  </p>
                </div>
              </div>
            ) : null}

            {!isHouseClosed ? (
              <div className="oc-hero-secondary">
                <button
                  type="button"
                  className="oc-hero-quick"
                  disabled={!canUseConsoleActions}
                  title={
                    canUseConsoleActions
                      ? "Edit house name and brand notes"
                      : "All partners must accept before house actions"
                  }
                  onClick={() => setBrandOpen(true)}
                >
                  <PenLine size={14} />
                  Brand
                </button>
                <Link
                  to={fixturesPath}
                  className={`oc-hero-quick ${!canUseConsoleActions ? "oc-hero-quick-disabled" : ""}`}
                  aria-disabled={!canUseConsoleActions}
                  onClick={(e) => {
                    if (!canUseConsoleActions) e.preventDefault();
                  }}
                  title={
                    canUseConsoleActions
                      ? "Manage fixtures on this house"
                      : "All partners must accept before house actions"
                  }
                >
                  <Calendar size={14} />
                  Fixtures
                  {upcomingFixturesCount > 0 ? (
                    <span className="oc-hero-quick-badge">{upcomingFixturesCount}</span>
                  ) : null}
                </Link>
                <button
                  type="button"
                  className="oc-hero-quick"
                  disabled={!canUseConsoleActions}
                  title={
                    canUseConsoleActions
                      ? "Edit min legs and stake limits"
                      : "All partners must accept before house actions"
                  }
                  onClick={() => setLimitsOpen(true)}
                >
                  <Settings2 size={14} />
                  Limits
                </button>
              </div>
            ) : null}
          </header>

          {syncDashboard && dashboardRefreshing && (
            <div className={`oc-sync-banner ${dashboardStale ? "oc-sync-banner-stale" : ""}`}>
              <Loader2 size={14} className="animate-spin" />
              {dashboardStale ? "Switching house — updating live data…" : "Syncing live dashboard…"}
            </div>
          )}

          {dashboardError && syncDashboard && (
            <div className="oc-sync-banner oc-sync-banner-error">
              {String(dashboardError)}
            </div>
          )}

          <div
            className={`oc-stage oc-stage-enter ${dashboardStale ? "oc-stage-stale" : ""}`}
          >
            {renderSection()}
          </div>
      </OperatorConsoleShell>

      <HouseBrandModal
        open={brandOpen}
        onClose={() => setBrandOpen(false)}
        bet={displayBet}
        operatorUserId={userId}
        onSuccess={handleRefresh}
      />
      <HouseLimitsModal
        open={limitsOpen}
        onClose={() => setLimitsOpen(false)}
        bet={displayBet}
        operatorUserId={userId}
        onSuccess={handleRefresh}
      />
      <HouseGoLiveModal
        open={goLiveOpen}
        loading={goLiveLoading}
        busy={statusBusy}
        error={goLiveError}
        readiness={goLiveReadiness}
        onClose={() => {
          if (statusBusy || goLiveLoading) return;
          setGoLiveOpen(false);
          setGoLiveReadiness(null);
          setGoLiveError(null);
        }}
        onConfirm={confirmGoLive}
      />
      <TreasuryMoveModal
        mode={treasuryMove === "withdraw" ? "withdraw" : "deposit"}
        open={treasuryMove != null}
        onClose={() => setTreasuryMove(null)}
        bet={displayBet}
        onSuccess={handleTreasurySuccess}
      />

      {statusToast ? (
        <div
          className={`oc-status-toast oc-status-toast-${statusToast.type}`}
          role="status"
        >
          {statusToast.type === "error" ? (
            <XCircle size={16} aria-hidden />
          ) : statusToast.type === "info" ? (
            <Clock size={16} aria-hidden />
          ) : (
            <CheckCircle2 size={16} aria-hidden />
          )}
          <span className="oc-status-toast-text">
            <strong>{statusToast.title}</strong>
            {statusToast.detail ? <em>{statusToast.detail}</em> : null}
          </span>
        </div>
      ) : null}
    </>
  );
};

export default OperatorConsole;
